import { Message } from "@/components/CodeGen/Chat/Message";
import { useToast } from "@/hooks/use-toast";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { getChatQueryOptions } from "@/lib/hooks/useChats";
import { useCurrentWebSocketError } from "@/lib/hooks/useCurrentWebSocketError";
import { getMcpServersQueryOptions } from "@/lib/hooks/useMcpServers";
import { getSecretsQueryOptions } from "@/lib/hooks/useSecrets";
import { useMessageStore } from "@/state/stores";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import type { FpMessage } from "../../../../schemas/codegen";

export const Route = createFileRoute("/(codegen)/codegen/$chatId")({
  component: ChatPage,
  loader: async ({ context: { queryClient }, params: { chatId } }) => {
    // Prefetch chat data, MCP servers, and secrets data in parallel
    await Promise.all([
      queryClient.ensureQueryData(getChatQueryOptions(chatId)),
      queryClient.ensureQueryData(getMcpServersQueryOptions(chatId)),
      queryClient.ensureQueryData(getSecretsQueryOptions(chatId)),
    ]);
  },
});

function ChatPage() {
  const chatId = useActiveChatId();
  const messages = useMessageStore((state) => state.messages);
  const getCurrentError = useCurrentWebSocketError();
  const { toast } = useToast();

  // This shouldn't happen, but is here to satisfy the type checker
  if (!chatId) {
    throw new Error("No chat ID available");
  }

  // Show toast when error occurs
  useEffect(() => {
    const errorEvent = getCurrentError?.(); // Only calls if error exists
    if (errorEvent) {
      toast({
        variant: "destructive",
        title: "Chat Error",
        description: errorEvent.error,
        duration: 5000,
      });
    }
  }, [getCurrentError, toast]);

  return (
    <div className="flex flex-col gap-5">
      {messages.map((message: FpMessage, index: number) => (
        <Message
          key={message.id}
          message={message}
          chatId={chatId}
          isFinalMessage={index === messages.length - 1}
        />
      ))}
    </div>
  );
}

export default ChatPage;
